import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, FileDown, FileSpreadsheet } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Sel, Field } from "./Sel";

const HEADERS = ["Name", "Company", "Email", "Phone", "City", "State", "Interested For", "Source", "Product / Service", "Priority", "Notes"];
const SAMPLE = ["Ravi Kumar", "Kumar Traders", "ravi@kumartraders.com", "+91 9876543210", "Jaipur", "Rajasthan", "Dealership", "Website", "Solar Panels", "high", "Wants dealership for Jaipur region"];

const downloadTemplate = () => {
  const csv = [HEADERS, SAMPLE].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = "greenrgy-lead-import-template.csv";
  a.click();
};

export const ImportLeadsDialog = ({ open, onOpenChange, onDone }) => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("round_robin");
  const [emp, setEmp] = useState("");
  const [emps, setEmps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const isAdmin = user.role === "admin";

  useEffect(() => {
    if (!open) return;
    setFile(null); setRes(null);
    if (isAdmin) api.get("/users").then((r) => setEmps(r.data.filter((u) => ["sales", "admin"].includes(u.role))));
  }, [open, isAdmin]);

  const upload = async () => {
    if (!file) return toast.error("Choose a CSV or Excel file");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("assign_mode", isAdmin ? mode : "self");
    if (mode === "employee") fd.append("assigned_to", emp);
    setBusy(true);
    try {
      const { data } = await api.post("/leads/import", fd);
      setRes(data);
      toast.success(`${data.created} leads imported`);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="import-leads-dialog">
        <DialogHeader>
          <DialogTitle>Import leads</DialogTitle>
          <DialogDescription>Upload a CSV or Excel (.xlsx) file. Duplicate phone/email rows are skipped.</DialogDescription>
        </DialogHeader>
        <button onClick={downloadTemplate} data-testid="download-template-btn" className="flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-800 w-fit">
          <FileDown className="h-4 w-4" /> Download sample template
        </button>
        <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 p-6 cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors">
          <FileSpreadsheet className="h-8 w-8 text-emerald-600" />
          <span className="text-sm font-medium" data-testid="import-file-name">{file ? file.name : "Click to choose file"}</span>
          <span className="text-xs text-slate-500">.csv, .xlsx · max 2000 rows</span>
          <input type="file" accept=".csv,.xlsx" className="hidden" data-testid="import-file-input" onChange={(e) => { setFile(e.target.files[0]); setRes(null); }} />
        </label>
        {isAdmin && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Assign leads"><Sel testid="import-assign-mode" value={mode} onChange={setMode} options={[{ value: "round_robin", label: "Round-robin (sales team)" }, { value: "employee", label: "Specific employee" }, { value: "none", label: "Unassigned" }]} /></Field>
            {mode === "employee" && <Field label="Employee"><Sel testid="import-assign-employee" value={emp} onChange={setEmp} placeholder="Select" options={emps.map((u) => ({ value: u.id, label: u.name }))} /></Field>}
          </div>
        )}
        {res && (
          <div className="rounded-xl border border-slate-200 p-4 text-sm space-y-2" data-testid="import-result">
            <div className="flex gap-4 font-semibold">
              <span>Total: {res.total}</span><span className="text-emerald-700" data-testid="import-created-count">Imported: {res.created}</span><span className="text-rose-600" data-testid="import-skipped-count">Skipped: {res.skipped.length}</span>
            </div>
            {res.skipped.length > 0 && (
              <ul className="max-h-32 overflow-y-auto text-xs text-slate-600 space-y-1 scroll-thin">
                {res.skipped.map((s, i) => <li key={i}>Row {s.row}: {s.reason}</li>)}
              </ul>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="import-close-btn">Close</Button>
          <Button onClick={upload} disabled={busy || !file || (mode === "employee" && !emp)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="import-submit-btn">
            <Upload className="h-4 w-4 mr-1" />{busy ? "Importing..." : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
